import { Injectable } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { Observable, of, ReplaySubject } from 'rxjs';
import { ContentService } from '@common/services/content.service';
import { CmsInfoItemsProvider } from '@common/components/info/cms-info-items-provider';
import { SpjpCommunityService } from '@spjp/services/community.service';
import { PhaseJpService } from '@lang/jp/services/phasejp.service';

export interface MenuItem {
    cmsKey: string;
    id: string;
    url?: string;
    isExternalLink?: boolean;
    children?: MenuItem[];
    link?: string;
}

@Injectable({
    providedIn: 'root',
})
export class MenuService {
    menuItems$: ReplaySubject<MenuItem[]> = new ReplaySubject<MenuItem[]>(1);

    get communityCode(): string {
        return this.communityService.communityCode;
    }

    get urlPrefix(): string {
        return `/${this.communityCode}`;
    }

    constructor(
        private contentService: ContentService,
        public communityService: SpjpCommunityService,
        private cmsInfoItemsProvider: CmsInfoItemsProvider,
        private phaseService: PhaseJpService,
    ) {
        this.initMenuItems();
    }

    getInfoItems$(): Observable<MenuItem[]> {
        return this.getFlatMenuItems$().pipe(
            map((items) => items.filter((i) => i?.url?.includes('/info/'))),
        );
    }

    getInfoItemSpecificUrlSegment$(id: string): Observable<string> {
        return this.contentService
            .valueExists(`info-item-${id}-url`)
            .pipe(
                switchMap((exists) =>
                    exists
                        ? this.contentService.value(`info-item-${id}-url`)
                        : of(id),
                ),
            );
    }

    private getFlatMenuItems$(): Observable<MenuItem[]> {
        return this.menuItems$.pipe(
            map((items) => {
                const flatMenuItems: MenuItem[] = [];
                items.forEach((item) => {
                    flatMenuItems.push(item);
                    if (item.children) {
                        flatMenuItems.push(...item.children);
                    }
                });
                return flatMenuItems;
            }),
        );
    }

    private initMenuItems() {
        let infoTabMenuItems = [];

        const responseMenuItems = this.cmsInfoItemsProvider.GetItemsWithMainKey(
            'more-information-items',
            'registration',
        );
        if (responseMenuItems && responseMenuItems.length > 0) {
            infoTabMenuItems = responseMenuItems;
        }

        infoTabMenuItems = infoTabMenuItems.map((item) => {
            const menuItem: MenuItem = {
                cmsKey: 'info-item-' + item.id + '-header',
                id: item.id,
                url:
                    this.urlPrefix + '/info/' + (item.url ? item.url : item.id),
            };
            return menuItem;
        });

        let menuItems = [
            {
                link: '/home',
                cmsKey: 'main-menu-home',
                id: 'menu-button-home',
            },
            {
                cmsKey: 'main-menu-info',
                id: 'main-menu-info',
                children: infoTabMenuItems,
            },
            {
                link: '/register',
                cmsKey:
                    'main-menu-' +
                    this.phaseService.getCmsAppendStringStatic +
                    'register',
                id: 'menu-button-register',
                children: null,
            },
            {
                link: '/suppliers',
                cmsKey: 'main-menu-suppliers',
                id: 'menu-button-suppliers',
            },
            {
                link: '/faq',
                cmsKey: 'main-menu-faq',
                id: 'menu-button-faq',
                children: null,
            },
            {
                link: '/contact',
                cmsKey: 'main-menu-contact',
                id: 'menu-button-contact',
                children: null,
            },
        ];

        this.communityService.isCommunityRegistrationEnabled$.subscribe(
            (isEnabled) => {
                if (!isEnabled) {
                    menuItems = menuItems.filter(
                        (item) => item.link !== '/register',
                    );
                }

                this.menuItems$.next(menuItems);
            },
        );
    }
}
